import { useId } from '@floating-ui/react';
import * as React from 'react';
import { NotificationsContext, NotificationType } from './context.js';

const DEFAULT_NOTIFICATION_TIMEOUT = 5000;
const DEFAULT_ERROR_NOTIFICATION_TIMEOUT = 10000;
let count = 0;
const getId = (id) => `${id}-${(count += 1)}`;
function useNotifications(options = {}) {
    const { global = false } = options;
    const { send, sendTopMost, dismiss, dismissTopMost } = React.useContext(NotificationsContext);
    const id = useId();
    const sendNotification = React.useCallback((notification) => {
        return global ? sendTopMost(notification) : send(notification);
    }, [send, sendTopMost, global]);
    const dismissNotification = React.useCallback((key) => {
        return global ? dismissTopMost(key) : dismiss(key);
    }, [dismiss, dismissTopMost, global]);
    const info = React.useCallback((notification) => sendNotification({
        type: NotificationType.Info,
        key: notification.key ?? getId(id),
        timeout: notification.timeout ?? DEFAULT_NOTIFICATION_TIMEOUT,
        ...notification,
    }), [sendNotification, id]);
    const error = React.useCallback((notification) => sendNotification({
        type: NotificationType.Error,
        key: notification.key ?? getId(id),
        timeout: notification.timeout ?? DEFAULT_ERROR_NOTIFICATION_TIMEOUT,
        ...notification,
    }), [sendNotification, id]);
    const warning = React.useCallback((notification) => sendNotification({
        type: NotificationType.Warning,
        key: notification.key ?? getId(id),
        timeout: notification.timeout ?? DEFAULT_ERROR_NOTIFICATION_TIMEOUT,
        ...notification,
    }), [sendNotification, id]);
    const success = React.useCallback((notification) => sendNotification({
        type: NotificationType.Success,
        key: notification.key ?? getId(id),
        timeout: notification.timeout ?? DEFAULT_NOTIFICATION_TIMEOUT,
        ...notification,
    }), [sendNotification, id]);
    return React.useMemo(() => ({
        info,
        error,
        warning,
        success,
        dismiss: dismissNotification,
    }), [info, error, warning, success, dismissNotification]);
}
function useAnnouncements(options = {}) {
    const { global = false } = options;
    const { send, sendTopMost, dismiss, dismissTopMost } = React.useContext(NotificationsContext);
    const id = useId();
    const sendAnnouncement = React.useCallback((message) => {
        return global ? sendTopMost(message) : send(message);
    }, [send, sendTopMost, global]);
    const dismissAnnouncement = React.useCallback((key) => {
        return global ? dismissTopMost(key) : dismiss(key);
    }, [dismiss, dismissTopMost, global]);
    const info = React.useCallback((announcement) => sendAnnouncement({
        type: NotificationType.Info,
        key: announcement.key ?? getId(id),
        ...announcement,
    }), [sendAnnouncement, id]);
    const error = React.useCallback((announcement) => sendAnnouncement({ type: NotificationType.Error, key: announcement.key ?? getId(id), ...announcement }), [sendAnnouncement, id]);
    const warning = React.useCallback((announcement) => sendAnnouncement({ type: NotificationType.Warning, key: announcement.key ?? getId(id), ...announcement }), [sendAnnouncement, id]);
    const success = React.useCallback((announcement) => sendAnnouncement({ type: NotificationType.Success, key: announcement.key ?? getId(id), ...announcement }), [sendAnnouncement, id]);
    return React.useMemo(() => ({
        info,
        error,
        warning,
        success,
        dismiss: dismissAnnouncement,
    }), [info, error, warning, success, dismissAnnouncement]);
}

export { useAnnouncements, useNotifications };
